/**
 * WorkflowProgress — the phase breadcrumb in the canvas header for the
 * Sales-Selection workflow.
 *
 * Renders only when `salesModeActive` is true (i.e. `selectionContext.branch`
 * is set, meaning `apply_application_profile` has resolved on the backend or
 * a chip click has seeded the branch optimistically).
 *
 * The five pips mirror the v6 flow phases so the breadcrumb stays in lock-step
 * with where the user actually is, and auto-advances as they answer:
 *
 *   ●━━━━●━━━━○━━━━○━━━━○   Power Tool · Application → Reduce → Requirements → Weighting → Shortlist
 *                                                                                     [Skip →]
 *
 * The active pip is driven by `selectionContext.activePhase` (reduction |
 * requirement | weighting | null) which the backend derives from which phase
 * still has unanswered questions — plus `stage` for the Application (pre-apply)
 * and Shortlist (all-answered) ends. Adding more questions to a phase deepens
 * that phase; it never adds a pip (funnel + breadcrumb both live at the phase
 * granularity). Labels are read-only. Skip hands back to the classic wizard.
 */

import { useLocation } from 'wouter-preact'

import { resetSelectionContext, salesModeActive, selectionContext } from '../../../state/chatStore.js'
import { t } from '../../../i18n/index.js'

const STAGE_ORDER = ['application', 'reduction', 'requirement', 'weighting', 'shortlist']

const STAGE_LABEL_KEY = {
  application: 'ion.workflow.stage.identify', // "Application"
  reduction: 'ion.workflow.stage.reduction',
  requirement: 'ion.workflow.stage.requirement',
  weighting: 'ion.workflow.stage.weighting',
  shortlist: 'ion.workflow.stage.shortlist',
}

/**
 * The active breadcrumb pip. `activePhase` (reduction|requirement|weighting) is
 * authoritative while collecting; `stage` covers the Application (no branch yet)
 * and Shortlist (all phase questions answered) ends. `null` activePhase means
 * every phase question is answered → Shortlist. Legacy contexts with only the
 * coarse `stage` fall back sensibly.
 */
function activeStageKey(ctx) {
  if (!ctx.branch) return 'application'
  if (ctx.stage === 'shortlist' || ctx.stage === 'trade_offs') return 'shortlist'
  const p = ctx.activePhase
  if (p === 'reduction' || p === 'requirement' || p === 'weighting') return p
  if (p === null) return 'shortlist' // all phase questions answered
  return 'reduction' // branch set, phase not yet resolved (legacy) → collecting
}

function stageState(currentIdx, idx) {
  if (idx < currentIdx) return 'completed'
  if (idx === currentIdx) return 'active'
  return 'pending'
}

export function WorkflowProgress() {
  if (!salesModeActive.value) return null

  const ctx = selectionContext.value
  const currentIdx = STAGE_ORDER.indexOf(activeStageKey(ctx))
  const branchLabel =
    ctx.branchLabel ||
    (ctx.branch ? t(`ion.workflow.branchLabel.${ctx.branch}`) : null)

  const [, navigate] = useLocation()

  function onSkip() {
    // F1: hands back to the classic wizard with no pre-fill. Full mapping of
    // selectionContext.hardConstraints → benchmarkingWorkflowState lives in
    // Phase 5 (F1-PLAN §6.6). Reset clears the breadcrumb so the user lands
    // in a clean wizard state without "ghost" progress.
    resetSelectionContext()
    navigate('/cell-selection?step=fundamentals')
  }

  return (
    <div
      class="ion-workflow-progress"
      role="navigation"
      aria-label={t('ion.workflow.progress.aria')}
    >
      {branchLabel && (
        <span class="ion-workflow-progress__branch" aria-label="Active branch">
          {branchLabel}
        </span>
      )}

      <ol class="ion-workflow-progress__stages">
        {STAGE_ORDER.map((stage, idx) => {
          const state = stageState(currentIdx, idx)
          return (
            <li
              key={stage}
              class={`ion-workflow-progress__stage ion-workflow-progress__stage--${state}`}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span class="ion-workflow-progress__dot" aria-hidden="true">
                {state === 'completed' ? '✓' : idx + 1}
              </span>
              <span class="ion-workflow-progress__label">
                {t(STAGE_LABEL_KEY[stage])}
              </span>
            </li>
          )
        })}
      </ol>

      <button
        type="button"
        class="ion-workflow-progress__skip"
        onClick={onSkip}
        aria-label={t('ion.workflow.skip.aria')}
      >
        {t('ion.workflow.skip.label')} →
      </button>
    </div>
  )
}
